<template>
  <AppLink :to="link">
    <div class="card">
      <div class="img-container">
        <img :src="resizedPhoto" :alt="slide.name" loading="lazy" />
      </div>
      <div class="card-content">
        <div class="region">{{ slide.name }}</div>
        <div class="count-and-price">
          <span v-if="toursCount">{{ t('offers', toursCount) }} ∙ </span>{{ t('priceFrom', { price: formattedPrice }) }}
        </div>
      </div>
    </div>
  </AppLink>
</template>

<script setup lang="ts">
import { useI18n } from '@experience/i18n';
import { CURRENCIES } from '@experience/money-utils';
import { formatTripsterMoney } from '@experience/money-utils/dist/formatMoney/formatMoney';
import { computed } from 'vue';

import { toursUrl } from '@/shared/helpers/urls';
import ru from '@/shared/i18n/ru';
import { type ToursCard } from '@/shared/models/tours';
import { imageResize } from '@/shared/utils/imageResize';

type MessageSchema = typeof ru;
const { t } = useI18n<{ message: MessageSchema }, 'ru'>({
  inheritLocale: true,
  messages: { ru },
});

const props = defineProps<{
  slide: Partial<ToursCard>;
  index: number;
}>();

const { toursCount, slug, countrySlug } = props.slide;

const link = countrySlug && slug ? toursUrl(countrySlug, slug) : '/';

const formattedPrice = computed(() => {
  const displayPrice = props.slide.displayPriceMin;

  if (displayPrice?.value && displayPrice.currency) {
    if (displayPrice.currency in CURRENCIES) {
      return formatTripsterMoney(displayPrice.value, displayPrice.currency as keyof typeof CURRENCIES);
    }

    return `${String(displayPrice.value)} ${displayPrice.currency}`.trim();
  }

  const price = props.slide.priceMin;
  if (!price) return price;

  return formatTripsterMoney(price, 'RUB');
});

const resizedPhoto = computed(() => imageResize('600x300', props.slide.photo ?? ''));
</script>

<style lang="scss" scoped>
.img-container {
  aspect-ratio: 4/3;
  overflow: hidden;
  border-radius: 16px;
}

.img-container:hover img {
  transform: scale(1.1);
}

img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
}

a {
  text-decoration: none;
}

.card-content {
  margin-top: var(--alias-space-6, 6px);
  color: var(--alias-color-text-default);
}

.region {
  @include typography.base-400-400-semi;
}

.count-and-price {
  @include typography.base-300-300-regular;

  @include adaptivity.tablet {
    @include typography.base-300-300-regular;
  }
}
</style>
